import SelectBox from 'devextreme-react/select-box';
import type { ChartThemeColors } from '../../charts/useChartThemeColors';
import type { UserActivityAnalyticsTechnologySection, UserActivityReportMetric } from '../types';
import type { UserActivityTechnologySubview } from '../userActivityPrefs';
import { DimensionReportPanel } from './DimensionReportPanel';
import { drilldownForDimension } from './sessionDrilldown';

const SUBVIEW_OPTIONS: { value: UserActivityTechnologySubview; text: string }[] = [
  { value: 'device', text: 'Device' },
  { value: 'browser', text: 'Browser' },
  { value: 'os', text: 'OS' },
  { value: 'viewport', text: 'Viewport bucket' },
  { value: 'screen', text: 'Screen bucket' },
  { value: 'pixelRatio', text: 'Pixel ratio' },
  { value: 'touch', text: 'Touch capability' },
  { value: 'build', text: 'Build' },
  { value: 'theme', text: 'Theme' }
];

export function TechnologyReport({
  technology,
  chartColors,
  subview,
  onSubviewChange,
  metric,
  onMetricChange,
  onDrilldown
}: {
  technology: UserActivityAnalyticsTechnologySection;
  chartColors: ChartThemeColors;
  subview: UserActivityTechnologySubview;
  onSubviewChange: (next: UserActivityTechnologySubview) => void;
  metric: UserActivityReportMetric;
  onMetricChange: (next: UserActivityReportMetric) => void;
  onDrilldown: (label: string, filter: unknown) => void;
}) {
  const rows =
    subview === 'browser'
      ? technology.byBrowser
      : subview === 'os'
        ? technology.byOs
        : subview === 'viewport'
          ? technology.byViewportBucket
          : subview === 'screen'
            ? technology.byScreenBucket
            : subview === 'pixelRatio'
              ? technology.byPixelRatioBucket
              : subview === 'touch'
                ? technology.byTouchCapable
                : subview === 'build'
                  ? technology.byBuild
                  : subview === 'theme'
                    ? technology.byTheme
                    : technology.byDevice;

  const canDrill =
    subview === 'device' ||
    subview === 'browser' ||
    subview === 'os' ||
    subview === 'build' ||
    subview === 'theme' ||
    subview === 'touch';

  return (
    <div className="user-activity-analytics__body user-activity-analytics__body--scroll">
      <div className="user-activity-analytics__chart-header">
        <div>
          <h3 className="ops-section-title" style={{ margin: 0 }}>
            Technology
          </h3>
          <p style={{ margin: '0.25rem 0 0', color: 'var(--ops-color-muted, #6b7280)', fontSize: 14 }}>
            Client hardware/software dimensions. Viewport/screen/DPR use size buckets for scale.
          </p>
        </div>
        <SelectBox
          dataSource={SUBVIEW_OPTIONS}
          value={subview}
          valueExpr="value"
          displayExpr="text"
          width={180}
          onValueChanged={(e) => onSubviewChange(e.value as UserActivityTechnologySubview)}
        />
      </div>

      <DimensionReportPanel
        title={SUBVIEW_OPTIONS.find((o) => o.value === subview)?.text ?? 'Technology'}
        rows={rows}
        colors={chartColors}
        metric={metric}
        onMetricChange={onMetricChange}
        fileNamePrefix={`tech-${subview}`}
        onRowDrilldown={
          canDrill
            ? (row) => {
                const drill = drilldownForDimension(
                  subview as 'device' | 'browser' | 'os' | 'build' | 'theme' | 'touch',
                  row.key,
                  row.label
                );
                onDrilldown(drill.label, drill.filter);
              }
            : undefined
        }
      />
    </div>
  );
}
